/**
 * Before/after image comparison slider. For each [data-hero-slider]: drag to move
 * the split (updating the clipped "before" image and the handle position), plus a
 * one-time auto-reveal on load. Vanilla port of the design mock's interaction.
 */

/**
 * Wire one before/after comparison slider: drag to move the split, plus a
 * one-time auto-reveal on load (skipped when the user prefers reduced motion).
 *
 * @param {Element} slider The [data-hero-slider] container element.
 */
function initSlider(slider) {
  const before = slider.querySelector('[data-hero-before]');
  const handle = slider.querySelector('[data-hero-handle]');
  if (!before || !handle) {
    return;
  }

  let pct = 64;
  let dragging = false;
  let raf = null;

  const apply = () => {
    before.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    handle.style.left = `${pct}%`;
  };

  const setFromEvent = (event) => {
    const rect = slider.getBoundingClientRect();
    if (!rect.width) {
      return;
    }
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    pct = Math.max(3, Math.min(97, x));
    apply();
  };

  slider.addEventListener('pointerdown', (event) => {
    dragging = true;
    if (raf) {
      cancelAnimationFrame(raf);
      raf = null;
    }
    setFromEvent(event);
  });

  document.addEventListener('pointermove', (event) => {
    if (dragging) {
      setFromEvent(event);
    }
  });

  document.addEventListener('pointerup', () => {
    dragging = false;
  });

  // Auto-reveal: sweep from fully covered (100) to the resting split (64).
  // Honour reduced-motion: snap straight to the resting split with no animation.
  const from = 100;
  const to = 64;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    pct = to;
    apply();
    return;
  }

  pct = from;
  apply();
  const duration = 1150;
  const start = performance.now();
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const tick = (now) => {
    const t = Math.min(1, (now - start) / duration);
    pct = from + (to - from) * easeOutCubic(t);
    apply();
    raf = t < 1 ? requestAnimationFrame(tick) : null;
  };
  raf = requestAnimationFrame(tick);
}

/**
 * Initialise every before/after hero slider on the page.
 */
export function initHeroSliders() {
  document.querySelectorAll('[data-hero-slider]').forEach(initSlider);
}
